import { useCallback } from "react";
import { cn } from "@/lib/utils";

/** Breathing room so the active chip never sits flush against the rail edge. */
const CHIP_SCROLL_PADDING_PX = 12;

export type CredentialSectionNavItem = {
  slug: string;
  label: string;
  attentionCount: number;
};

function AttentionCount({
  count,
  isActive,
}: {
  count: number;
  isActive: boolean;
}) {
  if (count === 0) {
    return null;
  }
  return (
    <span
      className={cn(
        "ml-auto inline-flex min-w-[20px] shrink-0 items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold",
        // On the filled active row the red badge would fight the blue, so it
        // switches to a translucent white pill the way the mobile chips do.
        isActive ? "bg-white/25 text-white" : "bg-red-50 text-red-700",
      )}
      aria-label={`${count} need${count === 1 ? "s" : ""} attention`}
    >
      {count}
    </span>
  );
}

/**
 * Category jump-list for the credentials page. Renders twice from one item
 * list: a sticky rail beside the sections on large screens, and a sticky
 * horizontally scrolling chip row above them on small screens, where there is
 * no room for a rail but the same "skip the scrolling" need exists.
 */
export function CredentialSectionNav({
  items,
  activeSlug,
  onSelect,
  compact = false,
}: {
  items: CredentialSectionNavItem[];
  activeSlug: string | null;
  onSelect: (slug: string) => void;
  compact?: boolean;
}) {
  // Attached only to the active chip, so it re-runs whenever the active
  // section changes and keeps that chip visible in the horizontal rail —
  // otherwise scrolling past a few sections leaves the highlight off-screen.
  // The rail's own scrollLeft is nudged rather than calling scrollIntoView,
  // which would cancel the page's in-flight smooth scroll after a jump.
  const activeChipRef = useCallback((chip: HTMLButtonElement | null) => {
    const rail = chip?.closest("ul");
    if (!chip || !rail) {
      return;
    }
    const chipBounds = chip.getBoundingClientRect();
    const railBounds = rail.getBoundingClientRect();
    if (chipBounds.left < railBounds.left) {
      rail.scrollLeft -=
        railBounds.left - chipBounds.left + CHIP_SCROLL_PADDING_PX;
    } else if (chipBounds.right > railBounds.right) {
      rail.scrollLeft +=
        chipBounds.right - railBounds.right + CHIP_SCROLL_PADDING_PX;
    }
  }, []);

  return (
    <>
      <nav
        aria-label="Credential sections"
        className={cn("hidden", !compact && "lg:block")}
      >
        {/* The rail sits on the page's gray background, so it needs the same
            white card surface as the sections to read as a control. */}
        <ul className="sticky top-4 space-y-1 rounded-xl border bg-white p-2">
          {items.map((item) => {
            const isActive = item.slug === activeSlug;
            return (
              <li key={item.slug}>
                <button
                  type="button"
                  onClick={() => onSelect(item.slug)}
                  aria-current={isActive ? "true" : undefined}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                    isActive
                      ? "bg-blue-600 font-semibold text-white shadow-sm"
                      : "font-medium text-slate-700 hover:bg-slate-100 hover:text-slate-900",
                  )}
                >
                  <span className="min-w-0 flex-1">{item.label}</span>
                  <AttentionCount
                    count={item.attentionCount}
                    isActive={isActive}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <nav
        aria-label="Credential sections"
        className={cn(
          "sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2",
          compact
            ? "-mx-4 px-4 sm:-mx-6 sm:px-6"
            : "-mx-2 px-2 md:-mx-4 md:px-4 lg:hidden",
        )}
      >
        <ul className="flex gap-2 overflow-x-auto pb-1">
          {items.map((item) => {
            const isActive = item.slug === activeSlug;
            return (
              <li key={item.slug}>
                <button
                  type="button"
                  ref={isActive ? activeChipRef : undefined}
                  onClick={() => onSelect(item.slug)}
                  aria-current={isActive ? "true" : undefined}
                  className={cn(
                    "flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors",
                    isActive
                      ? "border-blue-600 bg-blue-600 font-semibold text-white"
                      : "border-gray-300 bg-white font-medium text-slate-600",
                  )}
                >
                  {item.label}
                  {item.attentionCount > 0 && (
                    <span
                      className={cn(
                        "inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-xs font-semibold",
                        isActive
                          ? "bg-white/25 text-white"
                          : "bg-red-50 text-red-700",
                      )}
                    >
                      {item.attentionCount}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}
